import { useEffect, useRef, useState } from "react";
import { Flag, ChevronLeft, ChevronRight, Clock, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { http } from "@/lib/api";

const fmt = (s) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const ExamRunner = ({ exam, onSubmit, busy }) => {
  const key = `cl_exam_${exam.id}`;
  const saved = JSON.parse(localStorage.getItem(key) || "null");
  const [answers, setAnswers] = useState(saved?.answers || (exam.saved_answers?.length === exam.questions.length ? exam.saved_answers : exam.questions.map(() => -1)));
  const [flags, setFlags] = useState(saved?.flags || []);
  const [essay, setEssay] = useState(saved?.essay || exam.saved_essay || "");
  const [i, setI] = useState(0);
  const [left, setLeft] = useState(() => Math.max(0, Math.round((new Date(exam.deadline) - Date.now()) / 1000)));
  const sent = useRef(false);
  const n = exam.questions.length + (exam.essay ? 1 : 0);

  const latest = useRef({ answers, essay });
  latest.current = { answers, essay };
  useEffect(() => { localStorage.setItem(key, JSON.stringify({ answers, flags, essay })); }, [answers, flags, essay, key]);
  useEffect(() => {
    const t = setTimeout(() => { if (!sent.current) http.put(`/pathway/exam/${exam.id}/progress`, { answers, essay }).catch(() => {}); }, 2500);
    return () => clearTimeout(t);
  }, [answers, essay, exam.id]);
  const submit = () => { if (sent.current) return; sent.current = true; onSubmit({ answers: latest.current.answers, essay: latest.current.essay }); };
  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((new Date(exam.deadline) - Date.now()) / 1000));
      setLeft(s);
      if (s === 0) { clearInterval(t); submit(); }
    }, 1000);
    return () => clearInterval(t);
  }, [exam.deadline]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = exam.questions[i];
  const toggleFlag = () => setFlags((f) => (f.includes(i) ? f.filter((x) => x !== i) : [...f, i]));
  const unanswered = answers.filter((a) => a < 0).length;

  return (
    <div data-testid="exam-runner">
      <div className="sticky top-[64px] z-30 flex flex-wrap items-center gap-4 rounded-2xl bg-[#FDFBF7]/95 backdrop-blur border border-[#E6DEC3] px-5 py-3">
        <span className={`inline-flex items-center gap-2 font-mono text-lg ${left < 300 ? "text-[#C0392B]" : "text-[#2C4A3E]"}`} data-testid="exam-timer"><Clock className="w-5 h-5" />{fmt(left)}</span>
        <span className="text-sm text-stone-500" data-testid="exam-progress">{answers.length - unanswered}/{answers.length} answered · {flags.length} flagged</span>
        <button onClick={() => window.confirm(unanswered ? `${unanswered} questions are unanswered. Submit anyway?` : "Submit your exam?") && submit()} disabled={busy}
          data-testid="exam-submit-btn" className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit exam</button>
      </div>
      <div className="grid lg:grid-cols-12 gap-8 mt-6">
        <div className="lg:col-span-8">
          {q ? (
            <article className="rounded-3xl bg-white/80 border border-[#E6DEC3] p-7" data-testid={`exam-q-${i}`}>
              <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-widest text-stone-500">Question {i + 1} of {exam.questions.length}</span>
                <button onClick={toggleFlag} data-testid="exam-flag-btn" data-flagged={flags.includes(i)} className={`inline-flex items-center gap-1.5 text-sm ${flags.includes(i) ? "text-[#C87D53]" : "text-stone-400"}`}><Flag className="w-4 h-4" /> {flags.includes(i) ? "Flagged" : "Flag for review"}</button></div>
              <p className="mt-4 text-stone-800 whitespace-pre-wrap leading-relaxed">{q.question}</p>
              <div className="grid gap-2 mt-5">
                {q.options.map((o, j) => (
                  <button key={j} onClick={() => setAnswers((a) => a.map((x, k) => (k === i ? j : x)))} data-testid={`exam-opt-${j}`}
                    className={`text-left px-4 py-3 rounded-xl border-2 text-sm transition-colors ${answers[i] === j ? "border-[#2C4A3E] bg-[#F1EADB]" : "border-[#E6DEC3] hover:border-[#2C4A3E]"}`}>
                    <b className="mr-2">{"ABCD"[j]}.</b>{o}</button>
                ))}
              </div>
            </article>
          ) : (
            <article className="rounded-3xl bg-white/80 border border-[#E6DEC3] p-7" data-testid="exam-essay">
              <span className="text-xs uppercase tracking-widest text-stone-500">Extended Response (about 45 minutes recommended)</span>
              <div className="lesson-md mt-3 text-sm"><ReactMarkdown>{exam.essay.passages}</ReactMarkdown></div>
              <p className="font-semibold text-[#2C4A3E] mt-4">{exam.essay.prompt}</p>
              <textarea value={essay} onChange={(e) => setEssay(e.target.value)} rows={14} maxLength={12000} data-testid="exam-essay-input"
                className="w-full mt-4 px-4 py-3 rounded-2xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
              <p className="text-xs text-stone-500 mt-1">{essay.trim().split(/\s+/).filter(Boolean).length} words</p>
            </article>
          )}
          <div className="flex justify-between mt-4">
            <button disabled={i === 0} onClick={() => setI(i - 1)} data-testid="exam-prev-btn" className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[#E6DEC3] text-sm disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Previous</button>
            <button disabled={i >= n - 1} onClick={() => setI(i + 1)} data-testid="exam-next-btn" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-40">Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <aside className="lg:col-span-4">
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-3">Question map</p>
          <div className="grid grid-cols-8 gap-1.5" data-testid="exam-navigator">
            {Array.from({ length: n }).map((_, k) => (
              <button key={k} onClick={() => setI(k)} data-testid={`exam-nav-${k}`}
                className={`h-9 rounded-lg text-xs font-medium relative ${k === i ? "ring-2 ring-[#C87D53]" : ""} ${k < answers.length ? (answers[k] >= 0 ? "bg-[#C8DCCB] text-[#2C4A3E]" : "bg-[#F1EADB] text-stone-500") : essay.trim() ? "bg-[#C8DCCB]" : "bg-[#F1EADB]"}`}>
                {k < answers.length ? k + 1 : "ER"}{flags.includes(k) && <Flag className="w-2.5 h-2.5 absolute top-0.5 right-0.5 text-[#C87D53]" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-stone-500 mt-3">Green = answered · flag = marked for review. The exam auto-submits when time runs out.</p>
        </aside>
      </div>
    </div>
  );
};
